import { useEffect, useId } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { Building2, CircleSlash, ShieldCheck, User, UserCheck } from 'lucide-react'
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Input,
  SegmentedControl,
} from '@/components/ui'
import { Avatar } from '@/components/common/Avatar'
import type { CompanyMember, UpdateCompanyMemberPayload } from '@/types/company.types'
import { FALLBACK_CURRENCY } from '@/lib/formatters'
import { useOrgCurrency } from '@/hooks/useOrgCurrency'
import { useUpdateCompanyMember } from '../hooks/useCompanies'
import {
  updateCompanyMemberSchema,
  type UpdateCompanyMemberFormValues,
} from '../schemas/company.schemas'

type MemberUser = {
  _id: string
  email?: string
  firstName?: string
  lastName?: string
}

function getMemberUserId(member: CompanyMember): string {
  return typeof member.userId === 'string'
    ? member.userId
    : (member.userId as MemberUser)._id
}

function getMemberDisplayName(member: CompanyMember): string {
  if (typeof member.userId === 'string') return member.userId
  const u = member.userId as MemberUser
  const name = [u.firstName, u.lastName].filter(Boolean).join(' ')
  return name || u.email || u._id
}

function getMemberEmail(member: CompanyMember): string | null {
  if (typeof member.userId === 'string') return null
  const email = (member.userId as MemberUser).email?.trim()
  if (!email) return null
  return email === getMemberDisplayName(member) ? null : email
}

function getCompensationType(member: CompanyMember): 'hourly' | 'salaried' {
  return member.effectiveCompensation?.compensationType === 'hourly'
    ? 'hourly'
    : 'salaried'
}

function hasCompanyOverride(member: CompanyMember): boolean {
  return (
    member.hasCompensationOverride === true ||
    member.effectiveCompensation?.source === 'company_override'
  )
}

function normalizeCurrencyCode(currency: string | undefined): string | undefined {
  const trimmed = currency?.trim()
  if (!trimmed) return undefined

  const normalized = trimmed.toUpperCase()
  return /^[A-Z]{3}$/.test(normalized) ? normalized : undefined
}

function formatMoney(value: number | undefined, currency: string | undefined): string {
  if (value === undefined || Number.isNaN(value)) return 'Not set'

  const normalizedCurrency = normalizeCurrencyCode(currency)

  try {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: normalizedCurrency || FALLBACK_CURRENCY,
      maximumFractionDigits: 2,
    }).format(value)
  } catch {
    const formattedNumber = new Intl.NumberFormat('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(value)

    return normalizedCurrency ? `${formattedNumber} ${normalizedCurrency}` : formattedNumber
  }
}

function getRateSummary(member: CompanyMember): string {
  const compensation = member.effectiveCompensation
  const type = getCompensationType(member)
  if (type === 'hourly') {
    return `${formatMoney(compensation?.hourlyRate, compensation?.currency)} / hour`
  }
  return `${formatMoney(compensation?.baseSalary, compensation?.currency)} / month`
}

function buildDefaultValues(member: CompanyMember): UpdateCompanyMemberFormValues {
  const compensationType = getCompensationType(member)
  const compensation = member.effectiveCompensation
  return {
    role: member.role,
    status: member.status,
    // Hidden rates can't be edited, so the override editor stays closed and unvalidated.
    setOverride: !member.payRatesHidden && hasCompanyOverride(member),
    override: {
      compensationType,
      baseSalary:
        compensationType === 'salaried' ? compensation?.baseSalary : undefined,
      hourlyRate:
        compensationType === 'hourly' ? compensation?.hourlyRate : undefined,
    },
  }
}

export interface EditCompanyMemberModalProps {
  companyId: string
  /** Shown in the identity header so the member is never edited out of context. */
  companyName?: string | null
  member: CompanyMember | null
  open: boolean
  onClose: () => void
  onSuccess: () => void
}

export function EditCompanyMemberModal({
  companyId,
  companyName,
  member,
  open,
  onClose,
  onSuccess,
}: EditCompanyMemberModalProps): React.ReactElement | null {
  const formId = useId()
  const updateMember = useUpdateCompanyMember()
  const { currency: orgCurrency } = useOrgCurrency()
  const form = useForm<UpdateCompanyMemberFormValues>({
    resolver: zodResolver(updateCompanyMemberSchema),
    defaultValues: {
      role: 'member',
      status: 'active',
      setOverride: false,
      override: { compensationType: 'salaried' },
    },
  })
  const { reset, control, handleSubmit, watch, setValue } = form

  useEffect(() => {
    if (!member) return
    reset(buildDefaultValues(member))
  }, [member, reset])

  const setOverride = watch('setOverride') ?? false
  const overrideType = watch('override.compensationType') ?? getCompensationType(member ?? {
    userId: '',
    companyId: '',
    role: 'member',
    status: 'active',
  })

  const onSubmit = (values: UpdateCompanyMemberFormValues): void => {
    if (!member) return
    const userId = getMemberUserId(member)
    const payload: UpdateCompanyMemberPayload = {
      role: values.role,
      status: values.status,
    }

    if (member.payRatesHidden) {
      // Rates were withheld from this viewer: leave the override untouched.
    } else if (values.setOverride && values.override) {
      payload.compensationOverride =
        values.override.compensationType === 'hourly'
          ? {
              compensationType: 'hourly',
              hourlyRate: values.override.hourlyRate as number,
            }
          : {
              compensationType: 'salaried',
              baseSalary: values.override.baseSalary as number,
            }
    } else if (hasCompanyOverride(member)) {
      payload.compensationOverride = null
    }

    updateMember.mutate(
      {
        companyId,
        userId,
        payload,
      },
      {
        onSuccess: () => {
          onSuccess()
          onClose()
        },
      }
    )
  }

  if (!member) return null

  const currentCompensationType = getCompensationType(member)
  const currentRateSummary = getRateSummary(member)
  const currentCurrency =
    normalizeCurrencyCode(member.effectiveCompensation?.currency) || orgCurrency
  const memberDisplayName = getMemberDisplayName(member)
  const memberEmail = getMemberEmail(member)
  const inheritedSummary = hasCompanyOverride(member)
    ? "Use the rate set on the user's profile."
    : `Currently ${currentRateSummary} · ${currentCompensationType === 'hourly' ? 'Hourly' : 'Salaried'}.`

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) onClose()
      }}
    >
      <DialogContent className="flex max-h-[94dvh] w-[calc(100vw-1rem)] max-w-[calc(100vw-1rem)] flex-col gap-0 overflow-hidden rounded-[20px] border border-gray-200 bg-white p-0 shadow-[0_24px_60px_rgba(15,23,42,0.18)] dark:border-gray-700 dark:bg-gray-800 sm:w-full sm:max-w-xl sm:rounded-[24px]">
        <Form {...form}>
          <form
            id={formId}
            onSubmit={handleSubmit(onSubmit)}
            className="flex min-h-0 flex-col"
          >
            {/* Identity header. The member being edited is context, not an
                editable field — rendering it as a read-only text input made it
                look broken. */}
            <DialogHeader className="gap-0 space-y-0 px-5 pb-4 pt-5 sm:px-6 sm:pt-6">
              <div className="flex min-w-0 items-center gap-3.5">
                <Avatar name={memberDisplayName} size="lg" />
                <div className="min-w-0">
                  <DialogTitle className="truncate text-lg font-semibold tracking-[-0.01em] text-gray-900 dark:text-gray-100">
                    {memberDisplayName}
                  </DialogTitle>
                  <DialogDescription className="mt-1 flex min-w-0 flex-wrap items-center gap-x-2 gap-y-0.5 text-xs text-gray-500 dark:text-gray-400">
                    {memberEmail ? (
                      <span className="truncate">{memberEmail}</span>
                    ) : null}
                    {memberEmail && companyName ? (
                      <span aria-hidden>·</span>
                    ) : null}
                    {companyName ? (
                      <span className="inline-flex min-w-0 items-center gap-1">
                        <Building2 className="h-3 w-3 shrink-0" aria-hidden />
                        <span className="truncate">{companyName}</span>
                      </span>
                    ) : null}
                  </DialogDescription>
                </div>
              </div>
            </DialogHeader>

            <div className="min-h-0 flex-1 overflow-y-auto">
              <div className="grid gap-5 px-5 py-5 sm:px-6">
                <FormField
                  control={control}
                  name="role"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Role in this company</FormLabel>
                      <FormControl>
                        <SegmentedControl
                          ariaLabel="Role in this company"
                          className="w-full"
                          options={[
                            { value: 'member', label: 'Member', icon: User },
                            { value: 'manager', label: 'Manager', icon: ShieldCheck },
                          ]}
                          value={field.value ?? 'member'}
                          onChange={field.onChange}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={control}
                  name="status"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Status</FormLabel>
                      <FormControl>
                        <SegmentedControl
                          ariaLabel="Status"
                          className="w-full"
                          options={[
                            { value: 'active', label: 'Active', icon: UserCheck },
                            { value: 'inactive', label: 'Inactive', icon: CircleSlash },
                          ]}
                          value={field.value ?? 'active'}
                          onChange={field.onChange}
                        />
                      </FormControl>
                      <FormDescription>
                        Inactive members stay in history but won&apos;t appear in active
                        workflows. Their People &amp; HR employment stays open; remove the
                        member or terminate them to end it.
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                {member.payRatesHidden ? (
                  <div className="grid gap-1">
                    <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                      Compensation
                    </span>
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      Hidden. Viewing or changing pay rates needs payroll or sensitive-data access.
                    </p>
                  </div>
                ) : (
                  <div className="grid gap-2">
                    <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                      Compensation
                    </span>
                    {/* Two named outcomes beat a checkbox: "inherit" was previously
                        only expressible as the absence of a tick. */}
                    <SegmentedControl
                      ariaLabel="Compensation source"
                      variant="cards"
                      options={[
                        {
                          value: 'inherit',
                          label: "Inherit from user's profile",
                          description: inheritedSummary,
                        },
                        {
                          value: 'override',
                          label: 'Set a company-specific rate',
                          description:
                            'Overrides the default when payroll runs for this company. Required if the user works for more than one company.',
                        },
                      ]}
                      value={setOverride ? 'override' : 'inherit'}
                      onChange={(next) =>
                        setValue('setOverride', next === 'override', {
                          shouldDirty: true,
                          shouldValidate: true,
                        })
                      }
                    />

                    {setOverride && (
                      <div className="mt-1 grid gap-4 rounded-xl border border-gray-200 bg-gray-50/80 p-4 dark:border-gray-700 dark:bg-gray-900/30">
                        <FormField
                          control={control}
                          name="override.compensationType"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Pay type</FormLabel>
                              <FormControl>
                                <SegmentedControl
                                  ariaLabel="Pay type"
                                  className="w-full"
                                  options={[
                                    { value: 'salaried', label: 'Salaried' },
                                    { value: 'hourly', label: 'Hourly' },
                                  ]}
                                  value={field.value ?? 'salaried'}
                                  onChange={(v) => {
                                    field.onChange(v)
                                    if (v === 'hourly') {
                                      setValue('override.baseSalary', undefined, {
                                        shouldValidate: true,
                                      })
                                    } else {
                                      setValue('override.hourlyRate', undefined, {
                                        shouldValidate: true,
                                      })
                                    }
                                  }}
                                />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />

                        {overrideType === 'hourly' ? (
                          <FormField
                            control={control}
                            name="override.hourlyRate"
                            render={({ field }) => (
                              <FormItem>
                                <FormLabel>Hourly rate</FormLabel>
                                <FormControl>
                                  <Input
                                    type="number"
                                    step="0.01"
                                    min="0"
                                    value={
                                      field.value === undefined || Number.isNaN(field.value)
                                        ? ''
                                        : field.value
                                    }
                                    onChange={(e) => {
                                      const v = e.target.value
                                      field.onChange(v === '' ? undefined : Number(v))
                                    }}
                                    placeholder="0.00"
                                    hint={`Per-hour amount in ${currentCurrency}.`}
                                  />
                                </FormControl>
                                <FormMessage />
                              </FormItem>
                            )}
                          />
                        ) : (
                          <FormField
                            control={control}
                            name="override.baseSalary"
                            render={({ field }) => (
                              <FormItem>
                                <FormLabel>Base salary</FormLabel>
                                <FormControl>
                                  <Input
                                    type="number"
                                    step="0.01"
                                    min="0"
                                    value={
                                      field.value === undefined || Number.isNaN(field.value)
                                        ? ''
                                        : field.value
                                    }
                                    onChange={(e) => {
                                      const v = e.target.value
                                      field.onChange(v === '' ? undefined : Number(v))
                                    }}
                                    placeholder="0.00"
                                    hint={`Monthly salary amount in ${currentCurrency}.`}
                                  />
                                </FormControl>
                                <FormMessage />
                              </FormItem>
                            )}
                          />
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>

            <DialogFooter className="shrink-0 px-5 py-4 sm:px-6">
              <Button type="button" variant="secondary" onClick={onClose}>
                Cancel
              </Button>
              <Button
                className="w-full sm:w-auto"
                form={formId}
                type="submit"
                loading={updateMember.isPending}
              >
                {updateMember.isPending ? 'Saving…' : 'Save changes'}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  )
}

EditCompanyMemberModal.displayName = 'EditCompanyMemberModal'
